import React, { useContext, useState } from "react";
import { NotificationManager } from "react-notifications";
import { Link } from "react-router-dom";
import { FilterContext } from "../FilterContextProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faTrash } from "@fortawesome/free-solid-svg-icons";
import useApi from "../Api/Api";
import useSales from "../hooks/useSales";
import InventoryLayout from "./InventoryLayout";
import Input from "../Components/Input";
import SmallInput from "../Components/SmallInput";
import Button from "../Components/Button";
import { UserContext } from "../Auth/UserContextProvider";
import cn from "classnames";

const ProductHistory = () => {
  const api = useApi();
  const { isAdmin } = useContext(UserContext);
  const { filterData } = useContext(FilterContext);
  const [errorMessage, setErrorMessage] = useState(false);
  const [alterSalesObj, setAlterSalesObj] = useState({});

  const {
    sales,
    pagy,
    currentPage,
    setCurrentPage,
    resetPagination,
    getSales,
    resultsLoading,
  } = useSales();

  const handlePageChange = (e) => {
    setCurrentPage(e.target.value);
  };

  const handleInputChange = ({ target: { name, value } }, sale) => {
    setAlterSalesObj({
      ...alterSalesObj,
      [sale.id]: {
        ...sale,
        [name]: value,
      },
    });
  };

  const revert = () => {
    const check = window.confirm(
      "Are you sure you want to revert? All unsaved changes will be lost."
    );
    if (check) {
      setAlterSalesObj({});
    }
  };

  const handlePutSales = (event) => {
    event.preventDefault();
    Promise.all(
      Object.values(alterSalesObj).map((sale) => {
        return api.put(`/sales/${sale.id}`, { sale });
      })
    )
      .then((response) => {
        NotificationManager.success(
          "Adjustment successfully made",
          "Success",
          2000
        );
        getSales(currentPage);
        setAlterSalesObj({});
        return response;
      })
      .catch((error) => {
        setErrorMessage(error.response.data);
      })
      .finally(() => {});
  };

  const deleteSale = (event, saleToDelete) => {
    event.preventDefault();
    const check = window.confirm(`Are you sure you want to delete this row?`);
    if (check) {
      api.delete(`sales/${saleToDelete.id}`).then(() => {
        getSales(currentPage);
      });
    }
  };

  const salesToRender = [...sales];

  const columns = [
    { label: "EVENT", value: "name" },
    { label: "DATE", value: "date" },
    { label: "TYPE", value: "reporting_type" },
    { label: "PRICE", value: "price" },
    { label: "COST", value: "cost" },
    { label: "PURCHASES", value: "purchases" },
    { label: "SOLD", value: "sold" },
    { label: "DISCOUNT", value: "discount_quantity" },
    { label: "DAMAGE", value: "damage_quantity" },
    { label: "MAIL ORDER", value: "mail_order" },
    { label: "TM COMP", value: "ctm_comp" },
    { label: "MGMT COMP", value: "management_comp" },
    { label: "ARTIST COMP", value: "band_comp_quantity" },
    { label: "UNAVAIL", value: "unavailable" },
    { label: "INV ADJUST", value: "inventory_adjust" },
    { label: "ACTION", value: "action" },
  ];

  return (
    <InventoryLayout>
      <div className="text-black bg-gray-100 pt-2">
        <div className="mt-8 mx-8 bg-white rounded-lg border">
          <div className="mx-12 flex justify-end text-sm pt-10">
            {Object.keys(alterSalesObj).length !== 0 && (
              <span className="-mb-12">
                <span className="mr-2">
                  <Button
                    className="button button-white"
                    onClick={revert}
                    text="Revert"
                  ></Button>
                </span>
                <Button
                  className="button"
                  onClick={handlePutSales}
                  text="Update"
                ></Button>
              </span>
            )}
          </div>
          <div className="float-right mt-8 mr-12">
            {errorMessage?.error && (
              <div className="ml-8 mt-4 text-red-500 pb-2">
                {errorMessage.error}
              </div>
            )}
          </div>
          <div>
            <div className="mx-12 font-medium text-sm">
              <Link to="/inventory/history">
                <FontAwesomeIcon icon={faArrowLeft}></FontAwesomeIcon> Back to
                adjustment history
              </Link>
            </div>
            <div className="py-2 mx-12 text-2xl font-medium">
              {salesToRender[0]?.product?.name} {salesToRender[0]?.product_size}{" "}
              adjustment history
            </div>
            {salesToRender.length > 0 ? (
              <form className="flex mx-2">
                {columns.map((attribute) => {
                  return (
                    <table
                      className={cn({
                        "w-1/8":
                          attribute.value === "name" ||
                          attribute.value === "date" ||
                          attribute.value === "cost" ||
                          attribute.value === "reporting_type",
                        "w-1/10": attribute.value === "cost",
                        "w-1/16":
                          attribute.value !== "name" &&
                          attribute.value !== "date" &&
                          attribute.value !== "price" &&
                          attribute.value !== "cost" &&
                          attribute.value !== "reporting_type",
                      })}
                      key={attribute.value}
                    >
                      <thead className="text-xs font-light">
                        <tr
                          className={cn("text-left border-b-2 h-11", {
                            "text-right": attribute.value === "price",
                            "text-center":
                              attribute.value !== "name" &&
                              attribute.value !== "date" &&
                              attribute.value !== "reporting_type" &&
                              attribute.value !== "price",
                          })}
                        >
                          <td className={attribute.value === "price" && "pr-2"}>
                            {attribute.label}
                          </td>
                        </tr>
                      </thead>
                      <tbody>
                        {salesToRender.map((sale, i) => {
                          const saleToRender = alterSalesObj[sale.id] || sale;
                          return (
                            <tr
                              key={saleToRender.id}
                              className={cn(
                                "border-b text-xs h-11 text-center",
                                {
                                  "bg-yellow-50": alterSalesObj[sale.id],
                                }
                              )}
                            >
                              {attribute.value === "name" && (
                                <td className="text-left">
                                  {saleToRender.purchases > 0
                                    ? "Purchase"
                                    : saleToRender.event?.name}
                                </td>
                              )}
                              {attribute.value === "date" && (
                                <>
                                  {saleToRender.reporting_type === "adjust" ? (
                                    <td className="text-left">
                                      <input
                                        type="date"
                                        name="date"
                                        id={`date_${i}`}
                                        className="border -ml-2 p-1 rounded-md"
                                        onChange={(event) =>
                                          handleInputChange(event, saleToRender)
                                        }
                                        value={saleToRender.date}
                                      />
                                    </td>
                                  ) : (
                                    <td className="text-left">
                                      {saleToRender.formattedDate}
                                    </td>
                                  )}
                                </>
                              )}
                              {attribute.value === "reporting_type" && (
                                <td className="text-left">
                                  {saleToRender.reporting_type
                                    .charAt(0)
                                    .toUpperCase() +
                                    saleToRender.reporting_type.slice(1)}
                                </td>
                              )}
                              {attribute.value === "price" && (
                                <td className="text-right pr-2">
                                  {saleToRender.price?.toLocaleString("en-US", {
                                    style: "currency",
                                    currency: "USD",
                                  })}
                                </td>
                              )}
                              {attribute.value === "cost" && (
                                <td>
                                  <Input
                                    name={attribute.value}
                                    id={`${attribute.value}_${i}`}
                                    onChange={(e) =>
                                      handleInputChange(e, saleToRender)
                                    }
                                    value={saleToRender[attribute.value]}
                                    textRight
                                  />
                                </td>
                              )}
                              {attribute.value === "sold" && (
                                <td>
                                  {saleToRender.reporting_type !== "show"
                                    ? 0
                                    : saleToRender.count_in +
                                      saleToRender.add +
                                      saleToRender.add_2 +
                                      saleToRender.add_3 +
                                      saleToRender.add_4 -
                                      saleToRender.count_out -
                                      saleToRender.damage_quantity -
                                      saleToRender.band_comp_quantity -
                                      saleToRender.ctm_comp -
                                      saleToRender.management_comp -
                                      saleToRender.discount_quantity}
                                </td>
                              )}
                              {attribute.value !== "name" &&
                                attribute.value !== "date" &&
                                attribute.value !== "reporting_type" &&
                                attribute.value !== "price" &&
                                attribute.value !== "cost" &&
                                attribute.value !== "sold" &&
                                attribute.value !== "action" && (
                                  <td>
                                    <SmallInput
                                      name={attribute.value}
                                      id={`${attribute.value}_${i}`}
                                      onChange={(e) =>
                                        handleInputChange(e, saleToRender)
                                      }
                                      value={saleToRender[attribute.value]}
                                      className=""
                                    />
                                  </td>
                                )}

                              {attribute.value === "action" && isAdmin && (
                                <td className="w-12 text-center pr-2">
                                  <button
                                    onClick={(e) => deleteSale(e, saleToRender)}
                                  >
                                    <FontAwesomeIcon
                                      icon={faTrash}
                                    ></FontAwesomeIcon>
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  );
                })}
              </form>
            ) : (
              <div className="mx-12 pb-16">
                {resultsLoading ? (
                  <>Results are loading...</>
                ) : (
                  <> There is no sales history for this product</>
                )}
              </div>
            )}
          </div>
        </div>
        {filterData.artist_id && (
          <>
            <footer className="text-sm mt-4 mx-8 flex justify-between">
              <div className="ml-1 mr-8">
                Showing <span className="font-bold">{pagy.from} </span>to
                <span className="font-bold"> {pagy.to} </span>of
                <span className="font-bold"> {pagy.count} </span>results
              </div>
              <div className="mr-8">
                Page {pagy.page} of {pagy.last}
              </div>
              <div className="flex -mt-2 mb-4">
                <span>
                  <Button
                    className={
                      pagy.page === 1
                        ? "button-white-disabled button-sm"
                        : "button button-white button-sm"
                    }
                    onClick={resetPagination}
                    text="First"
                    disabled={pagy.page === 1}
                  ></Button>
                </span>
                <span className="mx-1">
                  <Button
                    value={pagy.prev}
                    className={
                      !pagy.prev
                        ? "button-white-disabled button-sm"
                        : "button button-white button-sm"
                    }
                    onClick={handlePageChange}
                    text="Prev"
                    disabled={!pagy.prev}
                  ></Button>
                </span>
                <span>
                  <Button
                    value={pagy.next}
                    className={
                      !pagy.next
                        ? "button-white-disabled button-sm"
                        : "button button-white button-sm"
                    }
                    onClick={handlePageChange}
                    text="Next"
                    disabled={!pagy.next}
                  ></Button>
                </span>
                <span className="ml-1">
                  <Button
                    value={pagy.last}
                    className={
                      pagy.page === pagy.last
                        ? "button-white-disabled button-sm"
                        : "button button-white button-sm"
                    }
                    onClick={handlePageChange}
                    text="Last"
                    disabled={pagy.page === pagy.last}
                  ></Button>
                </span>
              </div>
            </footer>
          </>
        )}
      </div>
    </InventoryLayout>
  );
};

export default ProductHistory;
